import UAParser from 'ua-parser-js';

import pkg from '../../../package.json';

declare const process: { env: Record<string, string | undefined> };

const PKG_VERSION = pkg.version;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MonitoringConfig {
  active?: boolean;
  applicationInsights?: {
    application?: string;
  };
}

interface MonitoringApp {
  bus: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- bus handlers accept varying payload shapes
    on(event: string, handler: (payload: any) => unknown): () => void;
    get(event: string, payload?: unknown): Promise<unknown>;
  };
  config: { name: string; uuid?: string; [key: string]: unknown };
  info: { wePkg: { version: string } };
  i18n: () => { language: string };
}

interface MonitoringLog {
  properties?: Record<string, unknown>;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// App Insights
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- ApplicationInsights type is dynamically imported
let appInsights: any;

const activateApplicationInsights = async (config: MonitoringConfig) => {
  // See https://ablcode.visualstudio.com/Atrius/_wiki/wikis/Wayfinder%20Webengine/86142/application-insights
  return process.env.APP_INSIGHTS
    ? import('@microsoft/applicationinsights-web').then(({ ApplicationInsights }) => {
        appInsights = new ApplicationInsights({
          config: {
            connectionString: process.env.APP_INSIGHTS,
            disableAjaxTracking: true,
            disableFetchTracking: true,
            disableCookiesUsage: true,
            cookieCfg: {
              enabled: false,
            },
          },
        });
        appInsights.loadAppInsights();

        const application = config.applicationInsights?.application;
        appInsights.addTelemetryInitializer((envelope: { data?: Record<string, unknown> }) => {
          envelope.data = envelope.data ?? {};
          envelope.data.version = PKG_VERSION;
          if (application !== undefined) {
            envelope.data.application = application;
          }
        });
      })
    : null;
};

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/**
 * Non-standard plugin lifecycle — called from app.ts before the plugin loop
 * so monitoring is active as early as possible.
 */
async function activate(config: MonitoringConfig) {
  if (config.active !== undefined && !config.active) {
    return;
  }

  if (config.applicationInsights) {
    await activateApplicationInsights(config);
  }
}

function create(app: MonitoringApp, config: MonitoringConfig) {
  if (config.applicationInsights && process.env.APP_INSIGHTS) {
    const trackEnrichedEvent = async (log: MonitoringLog) => {
      const shortAccountId = await app.bus.get('venueData/getAccountId');
      const parsedUserAgent = new UAParser().getResult();

      appInsights.trackEvent({
        ...log,
        properties: {
          url: window.location.href,
          instanceName: app.config.name,
          shortAccountId,
          productVersion: app.info.wePkg.version,
          deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          deviceOSName: parsedUserAgent.os.name,
          deviceOSVersion: parsedUserAgent.os.version,
          uiLocale: app.i18n().language,
          browserName: parsedUserAgent.browser.name,
          browserVersion: parsedUserAgent.browser.version,
          webConfigId: app.config.uuid,
          location: typeof window !== 'undefined' ? window.location.origin : 'server',
          ...log.properties,
        },
      });
      appInsights.flush();
    };

    app.bus.on('appInsights/log', (log: MonitoringLog) => trackEnrichedEvent(log));

    // Measure time from browser navigation start (performance.timeOrigin) to the
    // first full render of the map (map/mapReadyToShow, which dismisses the loading
    // screen). One-shot: self-unsubscribes so floor changes don't re-report.
    const stopLoadTimer = app.bus.on('map/mapReadyToShow', () => {
      stopLoadTimer();
      trackEnrichedEvent({
        name: 'app/initialLoadTime',
        properties: {
          loadTimeMs: Math.round(performance.now()),
        },
      });
    });
  }

  return {
    init: () => {}, // noop
  };
}

export { create, activate };
